<template>
  <div class="auth-manager">
    <LoginScreen
      v-if="!isAuthenticated && !isLoading"
      @login-success="handleLoginSuccess"
    />
    <div v-if="error" class="error-message">
      {{ error }}
      <a-button type="link" @click="clearError">閲嶈瘯</a-button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from 'vue';
import LoginScreen from './LoginScreen.vue';

export default defineComponent({
  name: 'AuthManager',
  components: {
    LoginScreen,
  },
  emits: ['auth-success'],
  setup(props, { emit }) {
    const isAuthenticated = ref(false);
    const isLoading = ref(false);
    const error = ref('');

    const restoreSession = async (): Promise<void> => {
      if (localStorage.getItem('user_logged_out') === '1') {
        isAuthenticated.value = false;
        isLoading.value = false;
        return;
      }

      isLoading.value = true;
      error.value = '';

      try {
        const response = await fetch('/api/session/current', {
          credentials: 'include',
        });

        if (!response.ok) {
          isAuthenticated.value = false;
          return;
        }

        const data = await response.json();
        if (data?.authenticated) {
          if (data.username) {
            sessionStorage.setItem('username', data.username);
          }
          isAuthenticated.value = true;
          emit('auth-success');
        }
      } catch {
        isAuthenticated.value = false;
      } finally {
        isLoading.value = false;
      }
    };

    const handleLoginSuccess = (loginInfo?: { username: string }): void => {
      localStorage.removeItem('user_logged_out');
      if (loginInfo) {
        sessionStorage.setItem('username', loginInfo.username);
      }
      isAuthenticated.value = true;
      emit('auth-success');
    };

    const clearError = (): void => {
      error.value = '';
    };

    onMounted(() => {
      void restoreSession();
    });

    return {
      isAuthenticated,
      isLoading,
      error,
      handleLoginSuccess,
      clearError,
    };
  },
});
</script>

<style scoped>
.auth-manager {
  position: relative;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.error-message {
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 82, 82, 0.15);
  border: 1px solid rgba(255, 82, 82, 0.4);
  border-radius: 4px;
  padding: 10px 16px;
  color: var(--error-color);
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: var(--shadow-md);
  z-index: 1000;
}
</style>
